import { Trial, TrialIngredient } from "../../../types/mes";
import { t, dateLocale, getLocale } from "../../../i18n/locale";

/** API/Prisma Decimal često stigne kao string */
export function bakerPctNum(v: unknown): number {
  if (v == null || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isFinite(n) ? n : 0;
}

export function sumBakerPct(ingredients: TrialIngredient[]): number {
  return ingredients.reduce((sum, i) => sum + bakerPctNum(i.BakerPercentage), 0);
}

export function getTrialId(trial: Trial): number {
  return trial.ID;
}

export function getTrialName(trial: Trial): string {
  return trial.TrialCode || t("Pokus {id}", { id: getTrialId(trial) });
}

export function formatTrialDate(dateStr?: string): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString(dateLocale());
}

function ingredientRowsHtml(ingredients: TrialIngredient[], doughMass: number): string {
  const totalPek = sumBakerPct(ingredients);
  const faktor = totalPek > 0 && doughMass > 0 ? doughMass / totalPek : 1;

  return ingredients
    .slice()
    .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0))
    .map((i) => {
      const pek = bakerPctNum(i.BakerPercentage);
      const kg = (pek * faktor).toFixed(3);
      const isNew = i.IsNewIngredient ? t("⚠️ NOVA") : "";
      return `
          <tr>
            <td>${i.IngredientName} ${isNew}</td>
            <td>${i.IngredientType || ""}</td>
            <td>${pek.toFixed(3)}</td>
            <td>${kg}</td>
            <td></td>
            <td>${i.Notes || ""}</td>
          </tr>
        `;
    })
    .join("");
}

function workOrderBodyHtml(trial: Trial, ingredients: TrialIngredient[]): string {
  const doughMass = bakerPctNum(trial.DoughMassKg);
  const ingredientRows = ingredientRowsHtml(ingredients, doughMass);
  const productName = trial.Product_ProductToTrial?.ProductName || "-";

  return `
  <h2>🧪 ${t("Radni nalog probe - {code}", { code: trial.TrialCode || "" })}</h2>
  <div class="meta">
    ${t("Datum")}: <b>${formatTrialDate(trial.TrialDate)}</b> |
    ${t("Proizvod")}: <b>${productName}</b> |
    ${t("Masa zamjesa")}: <b>${doughMass.toFixed(2)} kg</b>
  </div>
  <div class="purpose">🎯 ${t("Svrha")}: ${trial.Purpose || "-"}</div>
  ${trial.Notes ? `<div class="meta">${t("Napomena")}: ${trial.Notes}</div>` : ""}

  <div class="section">${t("Receptura")}</div>
  <table>
    <tr>
      <th style="width:30%">${t("Sirovina")}</th>
      <th style="width:12%">${t("Tip")}</th>
      <th style="width:10%">${t("PEK %")}</th>
      <th style="width:12%">${t("Kg / zamjes")}</th>
      <th style="width:18%">${t("LOT")}</th>
      <th style="width:18%">${t("Napomena")}</th>
    </tr>
    ${ingredientRows}
  </table>

  <div class="section" style="margin-top:24px">${t("Pracenje - Mikser")}</div>
  <table class="tracking">
    <tr>
      <th>${t("Batch ID")}</th><th>${t("Start")}</th><th>${t("Kraj mij.")}</th><th>${t("Kraj mase")}</th>
      <th>${t("Temp (°C)")}</th><th>${t("pH start.")}</th><th>${t("pH zamj.")}</th><th>${t("pH Rheon")}</th>
      <th>${t("Odstupanja")}</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">${t("Pracenje - Fermentacija")}</div>
  <table class="tracking">
    <tr>
      <th>${t("Batch ID")}</th><th>${t("Vis. mjerica (Mikser)")}</th><th>${t("Vis. mjerica (Rheon)")}</th><th>${t("Napomena")}</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">${t("Pracenje - Pecenje")}</div>
  <table class="tracking">
    <tr>
      <th>${t("Batch ID")}</th><th>${t("Start")}</th><th>${t("Kraj")}</th><th>${t("Temp (°C)")}</th><th>${t("Vis. mjerica")}</th><th>${t("Napomena")}</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>

  <div class="section" style="margin-top:16px">${t("Pracenje - Kvaliteta")}</div>
  <table class="tracking">
    <tr>
      <th>${t("Batch ID")}</th><th>${t("Spec. vol (L/kg)")}</th><th>${t("Skart (kom)")}</th>
      <th>${t("Skart boja")}</th><th>${t("Skart oblik")}</th><th>${t("Napomena")}</th>
    </tr>
    <tr><td>${trial.TrialCode}</td><td></td><td></td><td></td><td></td><td></td></tr>
  </table>`;
}

const PRINT_STYLES = `
    body { font-family: Arial, sans-serif; font-size: 11pt; margin: 15mm; }
    h2 { margin-bottom: 4px; }
    .meta { color: #555; margin-bottom: 10px; font-size: 10pt; }
    .purpose { background: #fff9e6; border-left: 4px solid #f39c12; padding: 6px 10px; margin-bottom: 12px; font-size: 10pt; }
    table { border-collapse: collapse; width: 100%; margin-top: 10px; }
    th { background: #2c3e50; color: white; padding: 6px 8px; text-align: left; font-size: 10pt; }
    td { border: 1px solid #ccc; padding: 5px 8px; font-size: 10pt; }
    tr:nth-child(even) { background: #f9f9f9; }
    .section { margin-top: 18px; font-weight: bold; font-size: 11pt; border-bottom: 1px solid #ccc; padding-bottom: 3px; }
    .tracking th { background: #1a5276; }
    .page-break { page-break-before: always; }
    @media print { body { margin: 10mm; } .page-break { page-break-before: always; } }
`;

function workOrderHtml(trial: Trial, ingredients: TrialIngredient[]): string {
  return `<!DOCTYPE html>
<html lang="${getLocale()}">
<head>
  <meta charset="UTF-8">
  <title>${t("Radni nalog probe - {code}", { code: trial.TrialCode || "" })}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
${workOrderBodyHtml(trial, ingredients)}
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;
}

export function printWorkOrder(trial: Trial, ingredients: TrialIngredient[]): boolean {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;
  printWindow.document.open();
  printWindow.document.write(workOrderHtml(trial, ingredients));
  printWindow.document.close();
  return true;
}

export function printBulkWorkOrders(
  trials: { trial: Trial; ingredients: TrialIngredient[] }[],
  date: Date
): boolean {
  const printWindow = window.open("", "_blank");
  if (!printWindow) return false;

  const pages = trials
    .map(({ trial, ingredients }, idx) => {
      const wrap = idx > 0 ? ' class="page-break"' : "";
      return `<div${wrap}>${workOrderBodyHtml(trial, ingredients)}</div>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html lang="${getLocale()}">
<head>
  <meta charset="UTF-8">
  <title>${t("Radni nalozi - {date}", { date: date.toLocaleDateString(dateLocale()) })}</title>
  <style>${PRINT_STYLES}</style>
</head>
<body>
  <div style="text-align: center; margin-bottom: 15px;">
    <h1 style="margin: 0;">${t("Radni nalozi proba - {date}", { date: date.toLocaleDateString(dateLocale()) })}</h1>
    <p style="color: #666; margin: 4px 0;">${t("{count} proba", { count: trials.length })}</p>
  </div>
  ${pages}
  <script>window.onload = function() { window.print(); }</script>
</body>
</html>`;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return true;
}
